import httpService from "./httpService";

export type AccountAccessGrantStatus =
  | "PENDING_APPROVAL"
  | "ACTIVE"
  | "REJECTED"
  | "CANCELLED"
  | "REVOKED";

export type AccountAccessGrantApprovalMode =
  | "SYSTEM_SELF"
  | "MANAGED_POOL"
  | "TARGET_OWNER";

export type AccountAccessGrantRelationship = "SELF" | "GRANTEE" | "TARGET";
export type AccountAccessGrantRelationshipFilter = Exclude<
  AccountAccessGrantRelationship,
  "SELF"
>;

export interface AccountAccessGrantUserSummary {
  userId: string;
  email: string;
  name: string | null;
  alias: string | null;
}

export interface ActorAccountAccessGrant {
  grantId: string;
  currentRequestId: string;
  status: AccountAccessGrantStatus;
  approvalMode: AccountAccessGrantApprovalMode;
  actorRelationship: AccountAccessGrantRelationship;
  statusChangedAtUtc: string;
  granteeUser: AccountAccessGrantUserSummary;
  targetUser: AccountAccessGrantUserSummary;
}

export interface ActorAccountAccessGrantListResponse {
  items: ActorAccountAccessGrant[];
}

export interface AccountAccessGrantListParams {
  relationship?: AccountAccessGrantRelationshipFilter;
  status?: AccountAccessGrantStatus;
}

export interface AccountAccessGrantTransitionInput {
  requestId: string;
  reason?: string;
}

export interface AccountAccessGrantTransitionResponse {
  grant: ActorAccountAccessGrant;
}

class AccountAccessGrantsService {
  private readonly baseUrl = "/v3/account-access-grants";

  async list(
    params: AccountAccessGrantListParams = {}
  ): Promise<ActorAccountAccessGrantListResponse> {
    const query = new URLSearchParams();
    if (params.relationship) {
      query.set("relationship", params.relationship);
    }
    if (params.status) {
      query.set("status", params.status);
    }

    const queryString = query.toString();
    const response = await httpService.get<ActorAccountAccessGrantListResponse>(
      queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl,
      { requestScope: "actor" }
    );
    return response.data;
  }

  async approveGrant(
    grantId: string,
    input: AccountAccessGrantTransitionInput
  ): Promise<AccountAccessGrantTransitionResponse> {
    return this.transition(grantId, "approve", input);
  }

  async rejectGrant(
    grantId: string,
    input: AccountAccessGrantTransitionInput
  ): Promise<AccountAccessGrantTransitionResponse> {
    return this.transition(grantId, "reject", input);
  }

  async revokeGrant(
    grantId: string,
    input: AccountAccessGrantTransitionInput
  ): Promise<AccountAccessGrantTransitionResponse> {
    return this.transition(grantId, "revoke", input);
  }

  private async transition(
    grantId: string,
    action: "approve" | "reject" | "revoke",
    input: AccountAccessGrantTransitionInput
  ): Promise<AccountAccessGrantTransitionResponse> {
    const reason = input.reason?.trim();
    const body = reason
      ? { requestId: input.requestId, reason }
      : { requestId: input.requestId };
    const response = await httpService.post<AccountAccessGrantTransitionResponse>(
      `${this.baseUrl}/${encodeURIComponent(grantId)}/${action}`,
      body,
      { requestScope: "actor" }
    );
    return response.data;
  }
}

export const accountAccessGrantsService = new AccountAccessGrantsService();
export default accountAccessGrantsService;
